import { useState, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { useToast } from "../../hooks/use-toast";
import { ClaimValidationAxiosInstance } from "../../Network/ClaimValidation";

interface ValidationHistoryRecord {
  date?: string;
  patient?: {
    patientName?: string;
    NationalIDNumber?: number | string;
    patientPassport?: string;
  };
  claim_ID?: number | string;
  validations?: any[];
  current_date?: string;
  request_date?: string;
  [key: string]: any;
}

interface ValidationHistoryResponse {
  success: boolean;
  history: ValidationHistoryRecord[];
}

interface FilterState {
  claim_ID: string;
  patient: string;
  patientPassport: string;
  nationalIDNumber: string;
  date: string;
  success: string;
}

const ValidationHistory: React.FC = () => {
  const { toast } = useToast();

  const [loading, setLoading] = useState<boolean>(false);
  const [historyData, setHistoryData] = useState<ValidationHistoryRecord[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const getLocalDateString = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [filters, setFilters] = useState<FilterState>({
    claim_ID: '',
    patient: '',
    nationalIDNumber: '',
    patientPassport: '',
    date: getLocalDateString(),
    success: ''
  });

  const itemsPerPage = 10;

  const ACTIVITY_TYPE_MAP: Record<string, string> = {
    "3": "CPT",
    "6": "Dental",
    "4": "HCPCS",
    "5": "Drugs",
  };

  const getActivityTypeName = (typeNumber: number | string): string => {
    const typeStr = String(typeNumber);
    return ACTIVITY_TYPE_MAP[typeStr] || "Unknown";
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleRowExpansion = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const fetchHistory = async (): Promise<void> => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (filters.claim_ID) params.append('claim_ID', filters.claim_ID);
      if (filters.patient) params.append('patient', filters.patient);
      if (filters.nationalIDNumber) params.append('nationalIDNumber', filters.nationalIDNumber);
      if (filters.patientPassport) params.append('patientPassport', filters.patientPassport);
      if (filters.date) params.append('date', filters.date);
      if (filters.success !== '') params.append('success', filters.success);
      params.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

      const response: AxiosResponse<ValidationHistoryResponse> = await ClaimValidationAxiosInstance.get(
        `history?${params.toString()}`
      );
      console.log('API Response:', response.data.history);
      console.log('First record patient:', response.data.history[0]?.patient);

      setHistoryData(response.data.history || []);
      setCurrentPage(1);
      setExpandedRows(new Set());

      if (response.data.history?.length === 0) {
        toast({
          title: "No Results",
          description: "No validation history found with the selected filters",
        });
      } else {
        toast({
          title: "Success",
          description: `Found ${response.data.history?.length || 0} validation record(s)`,
        });
      }
    } catch (error: any) {
      console.error('Error fetching validation history:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to fetch validation history",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = (): void => {
    setFilters({
      claim_ID: '',
      patient: '',
      patientPassport: '',
      nationalIDNumber: '',
      date: getLocalDateString(),
      success: ''
    });
    setHistoryData([]);
    setCurrentPage(1);
    setExpandedRows(new Set());
  };

  const clearHistory = async (): Promise<void> => {
    try {
      setClearing(true);
      const response = await ClaimValidationAxiosInstance.delete('clear-history');
      if (response.data.success) {
        toast({
          title: "Success",
          description: `History cleared successfully. ${response.data.deletedCount} record(s) deleted.`,
        });
        setHistoryData([]);
        setCurrentPage(1);
        setExpandedRows(new Set());
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to clear history",
        variant: "destructive"
      });
    } finally {
      setClearing(false);
      setShowClearConfirm(false);
    }
  };

  const totalPages = Math.ceil(historyData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = historyData.slice(startIndex, startIndex + itemsPerPage);

  const passedCount = historyData.filter(r =>
    r.validations?.every(v => v.result?.success !== false)
  ).length;
  const failedCount = historyData.length - passedCount;

  useEffect(() => {
    fetchHistory();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 pt-24 px-8 pb-8">
      <div className="max-w-7xl mx-auto space-y-6">

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b" style={{ background: 'linear-gradient(to right, #011934, #013a5c)' }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <i className="fas fa-filter text-white"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Search Filters</h2>
                <p className="text-xs text-white/80">Filter validation records by various criteria</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
              <div>
                <label htmlFor="claim_ID" className="block text-sm font-semibold text-gray-700 mb-2">
                  Claim ID
                </label>
                <input
                  type="text"
                  id="claim_ID"
                  name="claim_ID"
                  value={filters.claim_ID}
                  onChange={handleFilterChange}
                  placeholder="Enter claim ID"
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': '#011934' } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = '#011934'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div>
                <label htmlFor="patient" className="block text-sm font-semibold text-gray-700 mb-2">
                  Patient Name
                </label>
                <input
                  type="text"
                  id="patient"
                  name="patient"
                  value={filters.patient}
                  onChange={handleFilterChange}
                  placeholder="Enter patient name"
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': '#011934' } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = '#011934'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div>
                <label htmlFor="nationalIDNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                  National ID
                </label>
                <input
                  type="text"
                  id="nationalIDNumber"
                  name="nationalIDNumber"
                  value={filters.nationalIDNumber}
                  onChange={handleFilterChange}
                  placeholder="Enter national ID"
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': '#011934' } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = '#011934'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div>
                <label htmlFor="patientPassport" className="block text-sm font-semibold text-gray-700 mb-2">
                  Patient Passport
                </label>
                <input
                  type="text"
                  id="patientPassport"
                  name="patientPassport"
                  value={filters.patientPassport}
                  onChange={handleFilterChange}
                  placeholder="Enter patient passport"
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': '#011934' } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = '#011934'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
                  Claim Date
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={filters.date}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': '#011934' } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = '#011934'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>

              <div>
                <label htmlFor="success" className="block text-sm font-semibold text-gray-700 mb-2">
                  Status
                </label>
                <select
                  id="success"
                  name="success"
                  value={filters.success}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                  style={{ '--tw-ring-color': '#011934' } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = '#011934'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                >
                  <option value="">All Status</option>
                  <option value="true">Success</option>
                  <option value="false">Failed</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={fetchHistory}
                disabled={loading}
                className="px-8 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: '#011934' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#013a5c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#011934'}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Searching...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search"></i>
                    Search
                  </>
                )}
              </button>

              <button
                onClick={clearFilters}
                disabled={loading}
                className="px-8 py-3 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <i className="fas fa-redo"></i>
                Reset Filters
              </button>

              <button
                onClick={() => setShowClearConfirm(true)}
                disabled={loading}
                className="px-8 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <i className="fas fa-trash"></i>
                Delete All History
              </button>
            </div>
          </div>
        </div>

        {historyData.length > 0 && (
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500">Total Records</p>
                  <p className="text-2xl font-semibold text-gray-800">{historyData.length}</p>
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#3b82f6' }}>
                  <i className="fas fa-database text-white"></i>
                </div>
              </div>
            </div>
          </div>
        )}

        {paginatedResults.length > 0 && (
          <div className="space-y-4">
            {paginatedResults.map((record, index) => {
              const actualIndex = startIndex + index;
              const isExpanded = expandedRows.has(actualIndex);
              const hasFailedValidations = record.validations?.some(v => v.result?.success === false);

              return (
                <div
                  key={actualIndex}
                  className="bg-white rounded-xl shadow-md border-2 border-gray-200 hover:border-gray-300 transition-all hover:shadow-lg"
                >
                  {/* Card Header */}
                  <div className="px-6 py-4 flex items-center justify-between border-b-2 border-gray-200 bg-white">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-sm text-white" style={{ backgroundColor: '#3b82f6' }}>
                        #{actualIndex + 1}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                          <i className="fas fa-file-invoice" style={{ color: '#011934' }}></i>
                          Claim: {record.claim_ID || 'N/A'}
                        </h3>
                        <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                          <i className="fas fa-user text-gray-400"></i>
                          Patient: {record.patient?.patientName || 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleRowExpansion(actualIndex)}
                        className="w-10 h-10 bg-white rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-all flex items-center justify-center"
                        style={{
                          borderColor: isExpanded ? '#011934' : '#d1d5db',
                          backgroundColor: isExpanded ? '#f0f9ff' : 'white'
                        }}
                      >
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-gray-600`}></i>
                      </button>
                    </div>
                  </div>

                  {/* Card Body - Summary */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">National ID</p>
                        <p className="text-sm font-medium text-gray-800">
                          {record.patient?.NationalIDNumber || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Patient Passport</p>
                        <p className="text-sm font-medium text-gray-800">
                          {record.patient?.patientPassport || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Primary Diagnosis</p>
                        <p className="text-sm font-medium text-gray-800">
                          {record.primary_diagnosis_code || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Activities</p>
                        <p className="text-sm font-medium text-gray-800">
                          {record.activities?.length || 0} activity(s)
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Claim Date</p>
                        <p className="text-sm font-medium text-gray-800">
                          {record.current_date ? new Date(record.current_date).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-6 pt-6 border-t-2 border-gray-200 space-y-6">

                        {/* Activities Section */}
                        {record.activities && record.activities.length > 0 && (
                          <div>
                            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                              <i className="fas fa-procedures" style={{ color: '#011934' }}></i>
                              Activities ({record.activities.length})
                            </h4>
                            <div className="space-y-2">
                              {record.activities.map((activity: any, idx: number) => (
                                <div key={idx} className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div>
                                      <span className="text-gray-500 font-semibold">ID:</span>
                                      <span className="ml-2 text-gray-800">{activity.id}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500 font-semibold">Type:</span>
                                      <span className="ml-2 text-gray-800">{getActivityTypeName(activity.type)}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500 font-semibold">Codes:</span>
                                      <span className="ml-2 text-gray-800">{activity.codes?.join(', ') || 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500 font-semibold">Quantity:</span>
                                      <span className="ml-2 text-gray-800">{activity.quantity}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Diagnosis Section */}
                        <div>
                          <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                            <i className="fas fa-stethoscope" style={{ color: '#011934' }}></i>
                            Diagnosis
                          </h4>
                          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-500 font-semibold">Primary:</span>
                                <span className="ml-2 text-gray-800">{record.primary_diagnosis_code || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 font-semibold">Secondary:</span>
                                <span className="ml-2 text-gray-800">
                                  {record.secondery_diagnosis_codes?.length
                                    ? record.secondery_diagnosis_codes.join(', ')
                                    : 'None'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Validations Section */}
                        {record.validations && record.validations.length > 0 && (
                          <div>
                            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                              <i className="fas fa-check-circle" style={{ color: '#011934' }}></i>
                              Validations ({record.validations.length})
                            </h4>
                            <div className="space-y-3">
                              {record.validations.map((validation: any, idx: number) => {
                                const isFailed = validation.result?.success === false;
                                return (
                                  <div
                                    key={idx}
                                    className={`border-2 rounded-xl p-4 ${isFailed
                                      ? 'bg-red-50 border-red-300'
                                      : 'bg-green-50 border-green-300'
                                      }`}
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <i className={`fas ${isFailed ? 'fa-times-circle text-red-600' : 'fa-check-circle text-green-600'}`}></i>
                                        <span className="font-bold text-gray-800">{validation.name}</span>
                                      </div>
                                      {validation.code && (
                                        <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                                          Code: {validation.code}
                                        </span>
                                      )}
                                    </div>

                                    <p className={`text-sm mb-2 ${isFailed ? 'text-red-700' : 'text-green-700'}`}>
                                      {validation.result?.message || 'No message'}
                                    </p>

                                    {validation.result?.level && (
                                      <div className="text-xs text-gray-600">
                                        <span className="font-semibold">Level:</span> {validation.result.level}
                                      </div>
                                    )}

                                    {validation.benefit && (
                                      <div className="text-xs text-gray-600 mt-1">
                                        <span className="font-semibold">Benefit:</span> {validation.benefit}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Encounter Section */}
                        {record.Encounter && record.Encounter.length > 0 && record.Encounter[0].Type && (
                          <div>
                            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                              <i className="fas fa-hospital" style={{ color: '#011934' }}></i>
                              Encounter Information
                            </h4>
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500 font-semibold">Type:</span>
                                  <span className="ml-2 text-gray-800">{record.Encounter[0].Type}</span>
                                </div>
                                {record.Encounter[0].Start && (
                                  <div>
                                    <span className="text-gray-500 font-semibold">Start:</span>
                                    <span className="ml-2 text-gray-800">{record.Encounter[0].Start}</span>
                                  </div>
                                )}
                                {record.Encounter[0].End && (
                                  <div>
                                    <span className="text-gray-500 font-semibold">End:</span>
                                    <span className="ml-2 text-gray-800">{record.Encounter[0].End}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-600 font-medium">
                Showing <span className="font-bold text-gray-800">{startIndex + 1}</span> to{' '}
                <span className="font-bold text-gray-800">{Math.min(startIndex + itemsPerPage, historyData.length)}</span> of{' '}
                <span className="font-bold text-gray-800">{historyData.length}</span> results
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-xl border-2 border-gray-300 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-xl font-semibold transition-all ${currentPage === pageNum
                        ? 'text-white shadow-md'
                        : 'border-2 border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      style={currentPage === pageNum ? { backgroundColor: '#011934' } : {}}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-xl border-2 border-gray-300 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && historyData.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-search text-4xl text-gray-300"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No History Found</h3>
            <p className="text-gray-600">Use the search filters above to find validation history records</p>
          </div>
        )}
      </div>

      {/* Clear History Confirmation Dialog */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-red-200 p-8 max-w-md w-full mx-4">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Clear All History</h3>
                <p className="text-sm text-gray-500 mt-1">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-red-800 font-medium">
                 You are about to permanently delete <strong>ALL</strong> validation history records from the database.
              </p>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to proceed? This will remove all validation history and cannot be recovered.
            </p>

            <div className="flex gap-3">
              <button
                onClick={clearHistory}
                disabled={clearing}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {clearing ? (
                  <><i className="fas fa-spinner fa-spin"></i> Clearing...</>
                ) : (
                  <><i className="fas fa-trash"></i> Yes, Clear All</>
                )}
              </button>
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={clearing}
                className="flex-1 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationHistory;